/** Exact one-test decision value under DECLARED finite-world assumptions.
 * No graph confidence enters this module. Utilities must share [0, 1] units;
 * tests are deterministic partitions with equal declared resource allowances.
 * A(P) = sum_y max_a sum_{h:T(h)=y} P(h) U(a,h). The common pre-test value
 * cancels when ranking. For fixed U,T, |A(P)-A(Q)| <= TV(P,Q).
 */
export interface DecisionModel {
  version: 1
  scope: string
  prior: number[]
  utilities: number[][]
  tests: { id: string; outcomes: number[] }[]
}

export function parseDecisionModel(value: unknown): DecisionModel | null {
  if (!value || typeof value !== 'object') return null
  const m = value as DecisionModel
  const unit = (n: unknown) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1
  if (
    m.version !== 1 ||
    typeof m.scope !== 'string' ||
    !m.scope.trim() ||
    m.scope.length > 4000 ||
    !Array.isArray(m.prior) ||
    m.prior.length < 2 ||
    m.prior.length > 256 ||
    !m.prior.every(unit) ||
    Math.abs(m.prior.reduce((a, b) => a + b, 0) - 1) > 1e-12 ||
    !Array.isArray(m.utilities) ||
    m.utilities.length < 2 ||
    m.utilities.length > 16 ||
    !m.utilities.every((u) => Array.isArray(u) && u.length === m.prior.length && u.every(unit)) ||
    !Array.isArray(m.tests) ||
    !m.tests.length ||
    m.tests.length > 64 ||
    !m.tests.every(
      (t) =>
        t &&
        typeof t.id === 'string' &&
        /^[a-zA-Z0-9_-]{1,100}$/.test(t.id) &&
        Array.isArray(t.outcomes) &&
        t.outcomes.length === m.prior.length &&
        t.outcomes.every((y) => Number.isInteger(y) && y >= 0 && y < 16),
    ) ||
    new Set(m.tests.map((t) => t.id)).size !== m.tests.length
  )
    return null
  // Tiny decimal normalization error must not become an unsound TV bound.
  const mass = m.prior.reduce((a, b) => a + b, 0)
  return {
    version: 1,
    scope: m.scope,
    prior: m.prior.map((p) => p / mass),
    utilities: m.utilities.map((u) => [...u]),
    tests: m.tests
      .map((t) => ({ id: t.id, outcomes: [...t.outcomes] }))
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
  }
}

export function decisionScore(model: DecisionModel, test: DecisionModel['tests'][number]) {
  const rows = Array.from({ length: Math.max(...test.outcomes) + 1 }, () =>
    model.utilities.map(() => 0),
  )
  for (let h = 0; h < model.prior.length; h++)
    for (let a = 0; a < model.utilities.length; a++)
      rows[test.outcomes[h]!]![a]! += model.prior[h]! * model.utilities[a]![h]!
  return rows.reduce((sum, row) => sum + Math.max(...row), 0)
}

export interface DecisionRanking {
  selected: string
  exactScores: number
  boundChecks: number
  comparisonVisits: number
  reusedBounds: number
  reset: boolean
}

/** Certificates live only in this closure; imported caches can never authorize
 * reuse. Each bound measures change from THAT score's prior, not the last step.
 * context binds the exact execution inputs and resource assumptions at the caller.
 */
export function createDecisionSelector() {
  let definition = ''
  let cache: { score: number; prior: number[] }[] = []
  return (value: unknown, context = ''): DecisionRanking | null => {
    const model = parseDecisionModel(value)
    if (!model) {
      definition = ''
      cache = []
      return null
    }
    const key = JSON.stringify({ ...model, prior: null, context })
    const reset = definition !== key
    let exactScores = 0,
      boundChecks = 0,
      comparisonVisits = 0
    if (reset) {
      definition = key
      cache = []
    }
    // Outward slack covers floating point summation and TV computation. Near
    // ties fall through to full evaluation and the same deterministic tie rule.
    const slack = 64 * Number.EPSILON * model.prior.length * model.utilities.length * 16
    const bounds = model.tests.map((test, i) => {
      const old = cache[i]
      if (!old) {
        const score = decisionScore(model, test)
        exactScores++
        cache[i] = { score, prior: [...model.prior] }
        return { lower: score, upper: score, exact: true }
      }
      boundChecks++
      const tv = model.prior.reduce((sum, p, h) => sum + Math.abs(p - old.prior[h]!), 0) / 2
      return { lower: old.score - tv - slack, upper: old.score + tv + slack, exact: false }
    })
    const best = (field: 'lower' | 'upper', uncertain = false) => {
      let b = -1
      bounds.forEach((row, i) => {
        if ((!uncertain || !row.exact) && (b < 0 || row[field] > bounds[b]![field])) b = i
      })
      return b
    }
    while (true) {
      const winner = best('lower')
      comparisonVisits += bounds.length
      if (
        bounds.every(
          (b, i) =>
            i === winner ||
            (i < winner ? bounds[winner]!.lower > b.upper : bounds[winner]!.lower >= b.upper),
        )
      )
        return {
          selected: model.tests[winner]!.id,
          exactScores,
          boundChecks,
          comparisonVisits,
          reusedBounds: bounds.filter((b) => !b.exact).length,
          reset,
        }
      const next = best('upper', true)
      // Full evaluation always certifies, including exact ties in lexical ID order.
      if (next < 0) throw new Error('Exact decision ranking did not terminate.')
      const score = decisionScore(model, model.tests[next]!)
      exactScores++
      cache[next] = { score, prior: [...model.prior] }
      bounds[next] = { lower: score, upper: score, exact: true }
    }
  }
}
